import { TravelCurrencyCode, useTravelRateComparisonsForTravelPlacesQuery, PriceComparisonFragmentDoc, PriceComparisonFragment } from '@graphql/generated';
import { ApolloClient, ApolloError, makeVar, useApolloClient, useReactiveVar } from '@apollo/client';
import { useEffect } from 'react';
import { useLocaleContext } from '@shared/core/i18n/LocaleContext';
import { addError } from '@shared/utils/logger';
import { useEventInfo } from '@shared/utils/eventInfo';
import { translatedPrice, roundUpMinorUnits } from '@shared/utils/accommodationPrice';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { formatRedirectURL } from '@shared/utils/formatting/redirectURL.formatter';
import { useCheckInCheckoutDates } from '../useCheckInCheckoutDates';
import { useTravelWhiteLabelCurrency } from '../useTravelWhiteLabelCurrency';
import { useApiMetadata } from '../useApiMetadata';

export const queryStatusVar = makeVar<{
  loading: boolean;
  error?: ApolloError;
}>({ loading: false, error: undefined });

export const useTravelPlacesPriceComparison = (joyPlaceIds: string[]) => {
  const { eventInfo } = useEventInfo();
  const eventDate = eventInfo?.finalizedDate?.dateString;
  const { checkIn: checkInDate, checkOut: checkOutDate } = useCheckInCheckoutDates({ eventDate });
  const [localeBasedCurrencyEnabled, isLocaleCurrencyFlagLoading] = useIsFeatureEnabled('localeBasedHotelCurrency');
  const [showNewPriceComparisonUI, showNewPriceComparisonUILoading] = useIsFeatureEnabled('enableAccommodationsPriceComparisonV2');
  const currencyCode = useTravelWhiteLabelCurrency();
  const placeIds = joyPlaceIds.map(id => ({ joyPlaceId: id }));
  const { deviceType, anonymousId, eventId, userCountryCode } = useApiMetadata();
  const {
    data,
    loading: travelRateComparisonsForTravelPlacesLoading,
    error,
  } = useTravelRateComparisonsForTravelPlacesQuery({
    batchMode: 'fast',
    variables: {
      payload: {
        currencyCode: localeBasedCurrencyEnabled && currencyCode ? currencyCode : TravelCurrencyCode.USD,
        dates: {
          checkIn: {
            high: checkInDate,
            low: checkInDate,
          },
          checkOut: {
            high: checkOutDate,
            low: checkOutDate,
          },
        },
        occupancies: {
          occupancies: [
            {
              adults: 2,
              childrenAges: [],
            },
          ],
          unitCount: 1,
        },
        placeIds,
        metadata: {
          eventId,
          deviceType,
          anonymousId,
          userCountryCode,
        },
      },
    },
    skip: !placeIds.length || isLocaleCurrencyFlagLoading || !showNewPriceComparisonUI,
    onError(error) {
      addError(error, {
        source: 'Price Comparison api failed',
        context: 'travelRateComparisonsForTravelPlaces',
        errorMessage: error.message,
        checkInDate,
        checkOutDate,
        currencyCode: localeBasedCurrencyEnabled && currencyCode ? currencyCode : TravelCurrencyCode.USD,
        joyPlaceIds: placeIds,
      });
    },
  });

  const isLoading = travelRateComparisonsForTravelPlacesLoading || isLocaleCurrencyFlagLoading || showNewPriceComparisonUILoading;

  useEffect(() => {
    queryStatusVar({
      loading: isLoading,
      error,
    });
  }, [error, isLoading]);

  return {
    data,
    loading: isLoading,
    error,
  };
};

const readFragmentFromCache = (client: ApolloClient<object>, joyPlaceId: string) => {
  const fragment = PriceComparisonFragmentDoc;
  const id = `TravelPlaceComparison:${joyPlaceId}`;
  const data = client.cache.readFragment<PriceComparisonFragment>({
    id,
    fragment,
  });

  return data;
};

export interface Rate
  extends Readonly<{
    provider: string;
    price: string;
    priceInMinorUnits: number;
    priceDigits: number;
    url: string;
  }> {}

export const useTravelPlacesPriceComparisonFragment = (
  travelPlaceId: string,
  skip?: boolean,
): {
  rates: Rate[];
  loading: boolean;
  error?: Error;
} => {
  const client = useApolloClient();
  const locale = useLocaleContext();
  const { loading, error } = useReactiveVar(queryStatusVar);
  const [redirectServiceEnabled] = useIsFeatureEnabled('guestLinkUseRedirectService');

  if (loading) {
    return { rates: [], loading: true };
  }
  if (error) {
    return { rates: [], loading: false, error: error };
  }
  if (skip) {
    return { rates: [], loading: false };
  }

  const data = readFragmentFromCache(client, travelPlaceId);

  const rates =
    data?.providers?.map(({ providerFQDN, providerSearchURL, nightlyBaseRate }) => {
      const roundedUpMinorUnits = roundUpMinorUnits(nightlyBaseRate.valueInMinorUnits, nightlyBaseRate.digits);

      return {
        provider: providerFQDN,
        priceDigits: nightlyBaseRate.digits,
        priceInMinorUnits: roundedUpMinorUnits,
        url: redirectServiceEnabled ? (formatRedirectURL({ url: providerSearchURL }) ?? '') : providerSearchURL,
        price: translatedPrice(roundedUpMinorUnits, nightlyBaseRate.digits, nightlyBaseRate.currency, locale.language),
      };
    }) ?? [];

  return { rates, loading: loading };
};

export const getTravelPlacesPriceComparisonFragment = (client: ApolloClient<object>, travelPlaceIds: ReadonlyArray<Maybe<string>>) => {
  const results: PriceComparisonFragment[] = [];

  travelPlaceIds.forEach(travelPlaceId => {
    if (!travelPlaceId) return;
    const data = readFragmentFromCache(client, travelPlaceId!);
    if (data) results.push(data);
  });

  return results;
};
